/**
 * MemoryPanel — cross-thread workspace memory UI.
 *
 * Lists pinned + auto-learned facts the AI uses to ground every conversation
 * in this workspace. Lets the user add an explicit fact, toggle archive,
 * or delete. Open from AI Studio sidebar.
 *
 * Mounted inside a Sheet so it doesn't fight the chat layout for space.
 * The `/remember <fact>` slash command from the composer hits the same
 * `POST /api/memory/facts` endpoint under the hood — this panel is the
 * "review my saved memories" surface.
 */
import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../../fetchInterceptor';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../ui/sheet';
import { Brain, Plus, Trash2, Loader2, Pin } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MemoryPanel({ workspaceId, open, onOpenChange }) {
  const [facts, setFacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const r = await apiFetch(`${API_URL}/api/memory/facts?workspace_id=${workspaceId}`);
      if (r.ok) {
        const data = await r.json();
        setFacts(data.facts || []);
      }
    } catch {
      toast.error('Could not load memories');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const addFact = async () => {
    const text = input.trim();
    if (!text || !workspaceId) return;
    setAdding(true);
    try {
      const r = await apiFetch(`${API_URL}/api/memory/facts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspace_id: workspaceId, content: text }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save memory');
        return;
      }
      const fact = await r.json();
      setFacts((prev) => [fact, ...prev]);
      setInput('');
      toast.success('Memory saved');
    } catch {
      toast.error('Network error');
    } finally {
      setAdding(false);
    }
  };

  const deleteFact = async (id) => {
    try {
      const r = await apiFetch(`${API_URL}/api/memory/facts/${id}`, { method: 'DELETE' });
      if (r.ok) {
        setFacts((prev) => prev.filter((f) => f.memory_id !== id));
        toast.success('Memory removed');
      }
    } catch {
      toast.error('Could not delete');
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-violet-500" /> Workspace Memory
          </SheetTitle>
          <SheetDescription>
            Facts the AI keeps in mind for every conversation in this workspace.
            Try the <code className="text-[11px] bg-muted px-1 rounded">/remember &lt;fact&gt;</code> command in chat too.
          </SheetDescription>
        </SheetHeader>

        <div className="flex items-center gap-2 mt-4">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addFact(); }}
            placeholder="e.g. My fiscal year ends June 30"
            data-testid="memory-input"
          />
          <Button
            size="sm"
            onClick={addFact}
            disabled={!input.trim() || adding}
            data-testid="memory-add-btn"
          >
            {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto mt-4 space-y-2" data-testid="memory-facts-list">
          {loading && (
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
            </div>
          )}
          {!loading && facts.length === 0 && (
            <div className="text-xs text-muted-foreground py-8 text-center">
              No memories yet. Pin something the AI should always know about your business.
            </div>
          )}
          {facts.map((f) => (
            <div
              key={f.memory_id}
              className="group flex items-start gap-2 rounded-md border border-border bg-card px-3 py-2 hover:bg-accent/40 transition-colors"
              data-testid={`memory-fact-${f.memory_id}`}
            >
              <Pin className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${f.kind === 'explicit' ? 'text-violet-500' : 'text-zinc-400'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-foreground break-words">{f.content}</p>
                <div className="flex items-center gap-1.5 mt-1">
                  <Badge variant="outline" className="text-[9px] py-0 px-1 h-4">
                    {f.kind === 'explicit' ? 'pinned' : 'auto'}
                  </Badge>
                  <span className="text-[10px] text-muted-foreground">
                    {new Date(f.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => deleteFact(f.memory_id)}
                className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-400 hover:text-red-500"
                data-testid={`memory-delete-${f.memory_id}`}
                title="Remove"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
